import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import CrmLayout from '@/modules/crm/components/CrmLayout';
import { useCrmContacts, useCrmSourceOptions } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';

export default function CrmContactsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toasts, hideToast } = useToast();
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [page, setPage] = useState(1);
  
  const { data, isLoading } = useCrmContacts(page, 20, null, search || null);
  const { data: sourceOptionsData } = useCrmSourceOptions();
  
  const contacts = data?.contacts || [];
  const pagination = data?.pagination || { page: 1, totalPages: 1 };
  
  // Source values are now human-readable, so just return the value directly
  const getSourceLabel = (source) => source || '-';

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    setSearchParams(params);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getContactName = (contact) => {
    const parts = [contact.firstName, contact.lastName].filter(Boolean);
    return parts.join(' ') || 'Unknown';
  };

  const getContactInitial = (contact) => {
    return contact.firstName?.charAt(0)?.toUpperCase() || '?';
  };

  return (
    <CrmLayout toasts={toasts} onCloseToast={hideToast}>
      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, company..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90"
          >
            Search
          </button>
        </form>
      </div>

      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-semibold text-gray-900">Contacts</h2>
          <div className="flex gap-3">
            <Link
              to="/crm/contacts/new"
              className="inline-flex items-center px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 font-medium text-sm"
            >
              + Add Contact
            </Link>
            <Link
              to="/crm/opportunities"
              className="inline-flex items-center px-4 py-2 text-zapt-purple border border-zapt-purple rounded-lg hover:bg-zapt-purple/5 font-medium text-sm"
            >
              View Opportunities
            </Link>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          People you're tracking as potential customers. Each contact can have multiple opportunities (deals/projects).
        </p>
      </div>

      {/* Contacts Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex gap-4">
                  <div className="h-10 bg-gray-200 rounded w-10"></div>
                  <div className="flex-1 h-10 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {search ? 'No contacts match your search' : 'No contacts yet'}
            </p>
            <Link
              to="/crm/contacts/new"
              className="inline-flex items-center px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90"
            >
              Add your first contact
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Company</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Source</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Opportunities</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Next Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link
                        to={`/crm/contacts/${contact.id}`}
                        className="flex items-center gap-3"
                      >
                        <div className="w-8 h-8 bg-zapt-purple/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-zapt-purple text-sm font-medium">
                            {getContactInitial(contact)}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 hover:text-zapt-purple block">
                            {getContactName(contact)}
                          </span>
                          {contact.email && (
                            <span className="text-xs text-gray-500">{contact.email}</span>
                          )}
                        </div>
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {contact.company || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {getSourceLabel(contact.source)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {contact.opportunityCount > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                          {contact.opportunityCount}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {contact.nextAction ? (
                        <div>
                          <span className="text-gray-900 block truncate max-w-[200px]">{contact.nextAction}</span>
                          {contact.nextActionDue && (
                            <span className={`text-xs ${new Date(contact.nextActionDue) < new Date() ? 'text-red-600' : 'text-gray-500'}`}>
                              Due: {formatDate(contact.nextActionDue)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} contacts)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={!pagination.hasPrevPage}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={!pagination.hasNextPage}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </CrmLayout>
  );
}
